import * as signalR from '@microsoft/signalr';
import { HubConnectionState, Subject } from '@microsoft/signalr';
import { NotificationEventType } from '@/libs/core/notifications/models/notification-event-type';
import { Notification } from '@/libs/core/notifications/models/notification';
import ApiRequestIds from '@/libs/core/api-request-ids';
import { getAccessToken } from 'axios-jwt';

class NotificationService {
    created: Subject<Notification> = new Subject<Notification>();
    updated: Subject<Notification> = new Subject<Notification>();
    deleted: Subject<Notification> = new Subject<Notification>();
    reconnected: Subject<number> = new Subject<number>();

    private connection!: signalR.HubConnection;
    private connectedGroups: string[] = [];

    private syncTime = new Date().getTime();

    async init() {
        const accessToken = getAccessToken();

        if (!accessToken) {
            return;
        }

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl((window as any).ApiService.baseUrl + '/events', {
                headers: {
                    Authorization: 'Bearer ' + accessToken,
                },
                withCredentials: true,
                accessTokenFactory: () => accessToken,
            })
            .withAutomaticReconnect()
            .build();

        try {
            await this.connection.start();
            await this.reconnectTenants();
            this.connection.onreconnected(() => {
                this.reconnectTenants();
                this.reconnected.next(this.syncTime)
            });
            this.registerEvents();
            this.setHeaders();
        } catch (error) {
            console.error(error);
        }
    }

    private registerEvents() {
        this.connection.on(NotificationEventType.ObjectAdded, (e) => {
            const parsedEvent = JSON.parse(e);
            console.log(parsedEvent);
            const ignoreNotification = this.popCorrelationId(parsedEvent.correlationId);
            if (!ignoreNotification) {
                this.created.next(parsedEvent);
            }
            this.syncTime = new Date().getTime();
        });
        this.connection.on(NotificationEventType.ObjectUpdated, (e) => {
            const parsedEvent: Notification = JSON.parse(e);
            const ignoreNotification = this.popCorrelationId(parsedEvent.correlationId);
            if (!ignoreNotification) {
                this.updated.next(parsedEvent);
            }
            this.syncTime = new Date().getTime();
        });
        this.connection.on(NotificationEventType.ObjectDeleted, (e) => {
            const parsedEvent = JSON.parse(e);
            const ignoreNotification = this.popCorrelationId(parsedEvent.correlationId);
            if (!ignoreNotification) {
                this.deleted.next(parsedEvent);
            }
            this.syncTime = new Date().getTime();
        });
    }

    public popCorrelationId(correlationId: string): boolean {
        const correlationIdIndex = ApiRequestIds.findIndex((id) => correlationId === id);
        if (correlationIdIndex >= 0) {
            ApiRequestIds.splice(correlationIdIndex, 1);
            return true;
        }
        return false;
    }

    private setHeaders() {
        (window as any).SignalClientId = this.connection.connectionId;
    }

    private async reconnectTenants() {
        if (this.connectedGroups.length > 0) {
            for (let i = 0; i < this.connectedGroups.length; i++) {
                if (i == this.connectedGroups.length - 1) {
                    await this.joinTenant(this.connectedGroups[i]);
                } else {
                    await this.leaveTenant(this.connectedGroups[i]);
                }
            }
        }
    }

    public async leaveTenants() {
        for (const group of this.connectedGroups) {
            await this.leaveTenant(group);
        }
    }

    public async joinTenant(tenantId: string) {
        if (this.connection.state == HubConnectionState.Connected) {
            await this.leaveTenants();
            await this.connection.send('JoinTenant', tenantId);
        }
        if (!this.connectedGroups.includes(tenantId)) {
            this.connectedGroups.splice(this.connectedGroups.indexOf(tenantId), 1);
        }
    }

    public async leaveTenant(tenantId: string) {
        if (this.connection.state == HubConnectionState.Connected) {
            if (this.connectedGroups.includes(tenantId)) {
                this.connectedGroups.splice(this.connectedGroups.indexOf(tenantId), 1);
            }
            await this.connection.send('LeveTenant', tenantId);
        }
    }
}

export default new NotificationService();
